import _ from 'lodash';
import { makeAutoObservable, runInAction } from 'mobx';
import apiManage, { apiManage_rbp } from '../../request';
import { mapErrorToI18nKey } from '../../constants/tool-tunnel-config';
import type {
    MenuStoreLicenseCodeRow,
    MenuStoreListQueryParams,
    MenuStoreListResponse,
    MenuStoreMenuListItem,
    MenuStoreTemplateMenuRow,
} from '../../models/menu-store.entity';
import GlobalNotification from '../../component/common/snackbar';
import i18n from '../../i18n';
import setLoading from '../../component/linear-determinate';
import { accountState } from '../my-account/account-state';
import {
    collectMenuStoreDishVisibilityPatches,
    countMenuStoreStructureDishes,
    formatMenuStoreDate,
    mergeMenuStoreListItemWithDetail,
    parseMenuStoreCreateLicenseCodeRows,
    parseMenuStoreCreateLicenseCodesResponse,
    parseMenuStoreLicenseCodesResponse,
    parseMenuStoreListResponse,
    parseMenuStoreMenuDetailResponse,
} from './menu-store.helpers';

/** 菜单商店一级页面：商店首页 | 我的菜单 */
export type MenuStoreRootView = 'store-home' | 'my-menus';

/** 我的菜单二级 Tab：已激活 | 我上传 */
export type MenuStoreMyMenusTab = 'activated' | 'uploaded';

/** 视图：列表 | 菜单详情 | 菜品详情 */
export type MenuStoreViewMode = 'list' | 'detail' | 'dish-detail';

/**
 * 菜单商店页 MobX：列表/我的菜单、详情、激活、移除（`PATCH .../status` + `delisted`，创建者与已激活购买方共用，后端分支）等。
 */
export class MenuStoreState {
    constructor() {
        makeAutoObservable(this);
    }

    /** 页面一级视图 */
    rootView: MenuStoreRootView = 'store-home';

    /** 我的菜单页二级 Tab */
    myMenusTab: MenuStoreMyMenusTab = 'activated';

    /** 列表 / 详情 */
    viewMode: MenuStoreViewMode = 'list';

    /** 浏览 Tab 下列表 */
    browseList: MenuStoreMenuListItem[] = [];

    /** 我上传侧列表（`GET /v3/menu-store/my-menus/uploaded` 分页累加） */
    myMenusUploadedList: MenuStoreMenuListItem[] = [];

    /** 已购/已激活侧列表（`GET /v3/menu-store/my-menus/purchased` 分页累加；可含 `delisted`） */
    myMenusPurchasedList: MenuStoreMenuListItem[] = [];

    /** 我上传侧 `total` */
    myMenusUploadedTotal = 0;

    /** 已购/已激活侧 `total` */
    myMenusPurchasedTotal = 0;

    browseLoading = false;

    /** 商店首页列表触底追加下一页时的请求态（与首屏 `browseLoading` 区分） */
    browseLoadingMore = false;

    myMenusLoading = false;

    /** 已激活侧触底加载下一页中 */
    myMenusPurchasedLoadingMore = false;

    /** 我上传侧触底加载下一页中 */
    myMenusUploadedLoadingMore = false;

    browseTotal = 0;

    myTotal = 0;

    /**
     * 商店首页已发布列表：每页条数（与 `pagination_per_page` 一致；触底加载下一页）。
     */
    readonly browsePageSize = 50;

    /**
     * 我的菜单单侧列表每页条数（`uploaded` / `purchased` 接口共用）。
     */
    readonly myMenusPageSize = 50;

    /**
     * 下一页待请求的页码：首屏成功后为 2，之后每成功追加一页 +1（与 `loadBrowseList` / `loadBrowseListMore` 同步）。
     */
    browseNextPageToLoad = 1;

    /** `purchased` 侧下一页页码（首屏成功后为 2） */
    myMenusPurchasedNextPageToLoad = 1;

    /** `uploaded` 侧下一页页码（首屏成功后为 2） */
    myMenusUploadedNextPageToLoad = 1;

    /**
     * 已提交的搜索关键词（商店列表请求 `name`、我的菜单列表前端过滤共用）。
     * 仅应在用户触发搜索或清空时更新，与输入框内未提交的草稿无关。
     */
    browseSearchName = '';

    /** 当前详情对应的列表项 */
    selectedMenu: MenuStoreMenuListItem | null = null;

    /**
     * 进入详情时的路径提示（Tab / 商店首页）；当接口未返回 **`purchased_by_me`** 或与 **`created_by`** 无法判定时，与 `resolveDetailSourceForItem` 组合使用。
     */
    detailSource: 'store-home' | 'my-activated' | 'my-uploaded' = 'store-home';

    /** 当前选中的菜品（用于菜品详情态）。 */
    selectedDish: MenuStoreTemplateMenuRow | null = null;

    /** 激活弹窗 */
    activateDialogOpen = false;

    /** 激活码输入（用户输入已购/已获得的 license） */
    activateLicenseInput = '';

    activateSubmitting = false;

    /** 「移除菜单」请求中（按菜单 id，`PATCH .../status` → `delisted`） */
    removingMenuId: string | null = null;

    /** 「移除菜单」二次确认弹窗是否打开 */
    removeMenuConfirmOpen = false;

    /** 编辑预览保存（批量 `PATCH .../dish-visibility`，body `items`）中 */
    previewSaveSubmitting = false;

    /** 已激活详情：「立刻使用」选择门店弹窗 */
    useNowDialogOpen = false;

    /** 已激活详情：「立刻使用」目标门店 string_id 列表（可多选，提交时写入 `restaurant_string_ids`） */
    useNowRestaurantStringIds: string[] = [];

    /** 已激活详情：「立刻使用」提交中 */
    useNowSubmitting = false;

    /** 「立刻使用」分发结果汇总弹窗是否打开 */
    useNowResultDialogOpen = false;

    /** 结果弹窗副标题：菜单名 + 上传日期（与 shell2-app `MenuStoreUseNowResultModal` 一致） */
    useNowResultSubtitle = '';

    /** 结果弹窗正文行（已本地化，顺序与 App：`submitUseNow` lines） */
    useNowResultDialogLines: string[] = [];

    /** 是否至少有门店同步成功（决定顶部成功/失败图标；与 App `hadSuccessfulSync`） */
    useNowResultHadSuccessfulSync = false;

    /** 我上传详情：授权码管理列表（`GET .../menu/{id}/license-codes`） */
    licenseCodeRows: MenuStoreLicenseCodeRow[] = [];

    /** 授权码列表加载中 */
    licenseCodesLoading = false;

    /** 生成授权码请求中 */
    generateLicenseSubmitting = false;

    /** 最近一次生成接口返回的码（用于生成弹窗展示） */
    displayGeneratedLicenseCodes: string[] = [];

    /** `GET /v3/menu-store/menu/{id}` 拉取详情中（详情页遮罩） */
    menuDetailLoading = false;

    /** 详情请求代数，用于丢弃过期的并发响应 */
    private menuDetailLoadSeq = 0;

    /** 切换到商店首页 */
    async openStoreHome() {
        this.menuDetailLoadSeq += 1;
        this.menuDetailLoading = false;
        this.rootView = 'store-home';
        this.viewMode = 'list';
        this.selectedMenu = null;
        this.selectedDish = null;
        /** 在 `await loadBrowseList` 之前即置为 true，避免授权通过后首帧仍显示空列表 */
        this.browseLoading = true;
        await this.loadBrowseList();
    }

    /** 切换到我的菜单页 */
    async openMyMenus() {
        this.menuDetailLoadSeq += 1;
        this.menuDetailLoading = false;
        this.rootView = 'my-menus';
        this.myMenusTab = 'activated';
        this.viewMode = 'list';
        this.selectedMenu = null;
        this.selectedDish = null;
        /** 与 `openStoreHome` 一致，避免切换后首帧无 loading */
        runInAction(() => {
            this.myMenusUploadedList = [];
            this.myMenusPurchasedList = [];
            this.myMenusUploadedTotal = 0;
            this.myMenusPurchasedTotal = 0;
            this.myMenusPurchasedNextPageToLoad = 1;
            this.myMenusUploadedNextPageToLoad = 1;
            this.myMenusPurchasedLoadingMore = false;
            this.myMenusUploadedLoadingMore = false;
        });
        await this.loadMyMenusPurchasedFirstPage();
    }

    /**
     * 切换我的菜单二级 Tab；目标侧尚无数据时拉取第 1 页。
     * @param tab - 已激活 | 我上传
     */
    setMyMenusTab(tab: MenuStoreMyMenusTab) {
        this.myMenusTab = tab;
        this.viewMode = 'list';
        this.selectedMenu = null;
        this.selectedDish = null;
        this.myTotal = tab === 'activated' ? this.myMenusPurchasedTotal : this.myMenusUploadedTotal;
        if (tab === 'uploaded' && this.myMenusUploadedList.length === 0) {
            void this.loadMyMenusUploadedFirstPage();
        } else if (tab === 'activated' && this.myMenusPurchasedList.length === 0) {
            void this.loadMyMenusPurchasedFirstPage();
        }
    }

    /**
     * 商店首页：提交搜索关键词并重新拉取已发布列表。
     * @param keyword - 当前搜索框提交值（含空串表示清空条件）
     */
    applyStoreBrowseSearch(keyword: string) {
        this.browseSearchName = keyword;
        void this.loadBrowseList();
    }

    /**
     * 我的菜单：仅更新已提交过滤词（前端过滤当前 Tab 列表，不调接口）。
     * @param keyword - 提交值；空串表示不过滤名称
     */
    applyMyMenusSearchFilter(keyword: string) {
        this.browseSearchName = keyword;
    }

    /**
     * 拉取商店首页已发布列表第 1 页（搜索、进入首页、从详情返回等场景均全量重置本地列表）。
     */
    async loadBrowseList() {
        this.browseLoading = true;
        this.browseLoadingMore = false;
        this.browseNextPageToLoad = 1;
        try {
            const params: MenuStoreListQueryParams = {
                status: 'published',
                pagination_page: 1,
                pagination_per_page: this.browsePageSize,
            };
            if (this.browseSearchName.trim()) {
                params.name = this.browseSearchName.trim();
            }
            const res = await apiManage.getMenuStoreMenus(params);
            if (_.get(res, 'error')) {
                runInAction(() => {
                    this.browseList = [];
                    this.browseTotal = 0;
                    this.browseNextPageToLoad = 1;
                });
                GlobalNotification.error(i18n.t('menu_store_msg_load_list_failed'));
                return;
            }
            const data = res as MenuStoreListResponse;
            runInAction(() => {
                this.browseList = data.list ?? [];
                this.browseTotal = data.total ?? 0;
                /** 下一页从 2 开始（仅 `loadBrowseListMore` 使用） */
                this.browseNextPageToLoad = 2;
            });
        } finally {
            runInAction(() => {
                this.browseLoading = false;
            });
        }
    }

    /**
     * 商店首页：滚动触底时拉取下一页并追加到 `browseList`（首屏未完成或已无更多数据时直接返回）。
     */
    async loadBrowseListMore() {
        if (this.rootView !== 'store-home') {
            return;
        }
        if (this.browseLoading || this.browseLoadingMore) {
            return;
        }
        /** 首屏尚未成功时由 `browseLoading` 负责，避免与第 1 页重复请求 */
        if (this.browseNextPageToLoad < 2) {
            return;
        }
        /** 含 total 为 0 且无行时，避免反复请求下一页 */
        if (this.browseList.length >= this.browseTotal) {
            return;
        }
        this.browseLoadingMore = true;
        try {
            const params: MenuStoreListQueryParams = {
                status: 'published',
                pagination_page: this.browseNextPageToLoad,
                pagination_per_page: this.browsePageSize,
            };
            if (this.browseSearchName.trim()) {
                params.name = this.browseSearchName.trim();
            }
            const res = await apiManage.getMenuStoreMenus(params);
            if (_.get(res, 'error')) {
                GlobalNotification.error(i18n.t('menu_store_msg_load_list_failed'));
                return;
            }
            const data = res as MenuStoreListResponse;
            const pageList = data.list ?? [];
            runInAction(() => {
                if (pageList.length === 0) {
                    /** 服务端无更多行时收敛 total，避免反复触底空请求 */
                    this.browseTotal = this.browseList.length;
                    return;
                }
                const existingIds = new Set(this.browseList.map((row) => row._id));
                /** 使用 `push` 保持 observable 数组引用，避免 Virtuoso `data` 整体替换导致滚回顶部 */
                for (const row of pageList) {
                    if (!existingIds.has(row._id)) {
                        this.browseList.push(row);
                        existingIds.add(row._id);
                    }
                }
                const serverTotal = data.total ?? 0;
                if (serverTotal > 0) {
                    this.browseTotal = serverTotal;
                }
                this.browseNextPageToLoad += 1;
            });
        } finally {
            runInAction(() => {
                this.browseLoadingMore = false;
            });
        }
    }

    /**
     * 已激活侧：从第 1 页拉取 `GET .../my-menus/purchased` 并替换本地列表。
     */
    async loadMyMenusPurchasedFirstPage() {
        this.myMenusPurchasedLoadingMore = false;
        this.myMenusPurchasedNextPageToLoad = 1;
        this.myMenusLoading = true;
        try {
            const res = await apiManage.getMenuStoreMyMenusPurchased({
                pagination_page: 1,
                pagination_per_page: this.myMenusPageSize,
            });
            if (_.get(res, 'error')) {
                runInAction(() => {
                    this.myMenusPurchasedList = [];
                    this.myMenusPurchasedTotal = 0;
                    this.myMenusPurchasedNextPageToLoad = 1;
                    if (this.myMenusTab === 'activated') {
                        this.myTotal = 0;
                    }
                });
                GlobalNotification.error(i18n.t('menu_store_msg_load_list_failed'));
                return;
            }
            const parsed = parseMenuStoreListResponse(res);
            if (!parsed) {
                runInAction(() => {
                    this.myMenusPurchasedList = [];
                    this.myMenusPurchasedTotal = 0;
                    this.myMenusPurchasedNextPageToLoad = 1;
                    if (this.myMenusTab === 'activated') {
                        this.myTotal = 0;
                    }
                });
                GlobalNotification.error(i18n.t('menu_store_msg_load_list_failed'));
                return;
            }
            runInAction(() => {
                this.myMenusPurchasedList = parsed.list ?? [];
                this.myMenusPurchasedTotal = parsed.total ?? 0;
                this.myMenusPurchasedNextPageToLoad = 2;
                if (this.myMenusTab === 'activated') {
                    this.myTotal = this.myMenusPurchasedTotal;
                }
            });
        } finally {
            runInAction(() => {
                this.myMenusLoading = false;
            });
        }
    }

    /**
     * 我上传侧：从第 1 页拉取 `GET .../my-menus/uploaded` 并替换本地列表。
     */
    async loadMyMenusUploadedFirstPage() {
        this.myMenusUploadedLoadingMore = false;
        this.myMenusUploadedNextPageToLoad = 1;
        this.myMenusLoading = true;
        try {
            const res = await apiManage.getMenuStoreMyMenusUploaded({
                pagination_page: 1,
                pagination_per_page: this.myMenusPageSize,
            });
            if (_.get(res, 'error')) {
                runInAction(() => {
                    this.myMenusUploadedList = [];
                    this.myMenusUploadedTotal = 0;
                    this.myMenusUploadedNextPageToLoad = 1;
                    if (this.myMenusTab === 'uploaded') {
                        this.myTotal = 0;
                    }
                });
                GlobalNotification.error(i18n.t('menu_store_msg_load_list_failed'));
                return;
            }
            const parsed = parseMenuStoreListResponse(res);
            if (!parsed) {
                runInAction(() => {
                    this.myMenusUploadedList = [];
                    this.myMenusUploadedTotal = 0;
                    this.myMenusUploadedNextPageToLoad = 1;
                    if (this.myMenusTab === 'uploaded') {
                        this.myTotal = 0;
                    }
                });
                GlobalNotification.error(i18n.t('menu_store_msg_load_list_failed'));
                return;
            }
            runInAction(() => {
                this.myMenusUploadedList = parsed.list ?? [];
                this.myMenusUploadedTotal = parsed.total ?? 0;
                this.myMenusUploadedNextPageToLoad = 2;
                if (this.myMenusTab === 'uploaded') {
                    this.myTotal = this.myMenusUploadedTotal;
                }
            });
        } finally {
            runInAction(() => {
                this.myMenusLoading = false;
            });
        }
    }

    /**
     * 已激活侧：触底追加下一页（`push` 保持 observable 数组引用，避免虚拟列表滚回顶部）。
     */
    async loadMyMenusPurchasedMore() {
        if (this.rootView !== 'my-menus' || this.myMenusTab !== 'activated') {
            return;
        }
        if (this.myMenusLoading || this.myMenusPurchasedLoadingMore) {
            return;
        }
        if (this.myMenusPurchasedNextPageToLoad < 2) {
            return;
        }
        if (this.myMenusPurchasedList.length >= this.myMenusPurchasedTotal) {
            return;
        }
        this.myMenusPurchasedLoadingMore = true;
        try {
            const res = await apiManage.getMenuStoreMyMenusPurchased({
                pagination_page: this.myMenusPurchasedNextPageToLoad,
                pagination_per_page: this.myMenusPageSize,
            });
            if (_.get(res, 'error')) {
                GlobalNotification.error(i18n.t('menu_store_msg_load_list_failed'));
                return;
            }
            const parsed = parseMenuStoreListResponse(res);
            if (!parsed) {
                GlobalNotification.error(i18n.t('menu_store_msg_load_list_failed'));
                return;
            }
            const pageList = parsed.list ?? [];
            runInAction(() => {
                if (pageList.length === 0) {
                    this.myMenusPurchasedTotal = this.myMenusPurchasedList.length;
                    return;
                }
                const existingIds = new Set(this.myMenusPurchasedList.map((row) => row._id));
                for (const row of pageList) {
                    if (!existingIds.has(row._id)) {
                        this.myMenusPurchasedList.push(row);
                        existingIds.add(row._id);
                    }
                }
                const serverTotal = parsed.total ?? 0;
                if (serverTotal > 0) {
                    this.myMenusPurchasedTotal = serverTotal;
                }
                this.myMenusPurchasedNextPageToLoad += 1;
                if (this.myMenusTab === 'activated') {
                    this.myTotal = this.myMenusPurchasedTotal;
                }
            });
        } finally {
            runInAction(() => {
                this.myMenusPurchasedLoadingMore = false;
            });
        }
    }

    /**
     * 我上传侧：触底追加下一页。
     */
    async loadMyMenusUploadedMore() {
        if (this.rootView !== 'my-menus' || this.myMenusTab !== 'uploaded') {
            return;
        }
        if (this.myMenusLoading || this.myMenusUploadedLoadingMore) {
            return;
        }
        if (this.myMenusUploadedNextPageToLoad < 2) {
            return;
        }
        if (this.myMenusUploadedList.length >= this.myMenusUploadedTotal) {
            return;
        }
        this.myMenusUploadedLoadingMore = true;
        try {
            const res = await apiManage.getMenuStoreMyMenusUploaded({
                pagination_page: this.myMenusUploadedNextPageToLoad,
                pagination_per_page: this.myMenusPageSize,
            });
            if (_.get(res, 'error')) {
                GlobalNotification.error(i18n.t('menu_store_msg_load_list_failed'));
                return;
            }
            const parsed = parseMenuStoreListResponse(res);
            if (!parsed) {
                GlobalNotification.error(i18n.t('menu_store_msg_load_list_failed'));
                return;
            }
            const pageList = parsed.list ?? [];
            runInAction(() => {
                if (pageList.length === 0) {
                    this.myMenusUploadedTotal = this.myMenusUploadedList.length;
                    return;
                }
                const existingIds = new Set(this.myMenusUploadedList.map((row) => row._id));
                for (const row of pageList) {
                    if (!existingIds.has(row._id)) {
                        this.myMenusUploadedList.push(row);
                        existingIds.add(row._id);
                    }
                }
                const serverTotal = parsed.total ?? 0;
                if (serverTotal > 0) {
                    this.myMenusUploadedTotal = serverTotal;
                }
                this.myMenusUploadedNextPageToLoad += 1;
                if (this.myMenusTab === 'uploaded') {
                    this.myTotal = this.myMenusUploadedTotal;
                }
            });
        } finally {
            runInAction(() => {
                this.myMenusUploadedLoadingMore = false;
            });
        }
    }

    /**
     * 双侧各从第 1 页并行拉取（激活成功、移除菜单、保存可见性等需两侧数据一致时调用）。
     */
    async refreshMyMenusListsFromFirstPage() {
        this.myMenusLoading = true;
        this.myMenusPurchasedLoadingMore = false;
        this.myMenusUploadedLoadingMore = false;
        try {
            const [pRes, uRes] = await Promise.all([
                apiManage.getMenuStoreMyMenusPurchased({
                    pagination_page: 1,
                    pagination_per_page: this.myMenusPageSize,
                }),
                apiManage.getMenuStoreMyMenusUploaded({
                    pagination_page: 1,
                    pagination_per_page: this.myMenusPageSize,
                }),
            ]);
            if (_.get(pRes, 'error') || _.get(uRes, 'error')) {
                runInAction(() => {
                    this.myMenusPurchasedList = [];
                    this.myMenusUploadedList = [];
                    this.myMenusPurchasedTotal = 0;
                    this.myMenusUploadedTotal = 0;
                    this.myMenusPurchasedNextPageToLoad = 1;
                    this.myMenusUploadedNextPageToLoad = 1;
                    this.myTotal = 0;
                });
                GlobalNotification.error(i18n.t('menu_store_msg_load_list_failed'));
                return;
            }
            const pParsed = parseMenuStoreListResponse(pRes);
            const uParsed = parseMenuStoreListResponse(uRes);
            if (!pParsed || !uParsed) {
                runInAction(() => {
                    this.myMenusPurchasedList = [];
                    this.myMenusUploadedList = [];
                    this.myMenusPurchasedTotal = 0;
                    this.myMenusUploadedTotal = 0;
                    this.myMenusPurchasedNextPageToLoad = 1;
                    this.myMenusUploadedNextPageToLoad = 1;
                    this.myTotal = 0;
                });
                GlobalNotification.error(i18n.t('menu_store_msg_load_list_failed'));
                return;
            }
            runInAction(() => {
                this.myMenusPurchasedList = pParsed.list ?? [];
                this.myMenusPurchasedTotal = pParsed.total ?? 0;
                this.myMenusPurchasedNextPageToLoad = 2;
                this.myMenusUploadedList = uParsed.list ?? [];
                this.myMenusUploadedTotal = uParsed.total ?? 0;
                this.myMenusUploadedNextPageToLoad = 2;
                this.myTotal =
                    this.myMenusTab === 'activated' ? this.myMenusPurchasedTotal : this.myMenusUploadedTotal;
            });
        } finally {
            runInAction(() => {
                this.myMenusLoading = false;
            });
        }
    }

    /**
     * 仅刷新当前 Tab 对应侧的第 1 页（从详情返回列表等）。
     */
    async reloadMyMenusCurrentTabFirstPage() {
        if (this.myMenusTab === 'activated') {
            await this.loadMyMenusPurchasedFirstPage();
        } else {
            await this.loadMyMenusUploadedFirstPage();
        }
    }

    /** 判定某菜单是否为「我上传的菜单」 */
    isUploadedMenu(item: MenuStoreMenuListItem): boolean {
        const uid = this.currentAccountId;
        if (!uid || !item.created_by) {
            return false;
        }
        return String(item.created_by) === uid;
    }

    /**
     * 我的菜单-已激活列表：绑定 `purchased` 侧。
     */
    get activatedMenus(): MenuStoreMenuListItem[] {
        return this.myMenusPurchasedList;
    }

    /**
     * 我的菜单-我上传列表：绑定 `uploaded` 侧并排除下架/驳回。
     */
    get uploadedMenus(): MenuStoreMenuListItem[] {
        return this.myMenusUploadedList.filter(
            (item) => item.status !== 'delisted' && item.status !== 'rejected'
        );
    }

    /** 进入详情（先展示列表项，再拉 `GET .../menu/{id}` 刷新完整详情） */
    openDetail(item: MenuStoreMenuListItem) {
        this.selectedMenu = item;
        this.selectedDish = null;
        if (this.rootView === 'my-menus') {
            this.detailSource = this.myMenusTab === 'activated' ? 'my-activated' : 'my-uploaded';
        } else {
            this.detailSource = 'store-home';
        }
        this.viewMode = 'detail';
        this.resetLicenseCodeUiState();
        this.menuDetailLoadSeq += 1;
        /** 当前次详情请求的序号，用于忽略乱序响应 */
        const seq = this.menuDetailLoadSeq;
        void this.fetchMenuStoreMenuDetail(seq, item);
    }

    /**
     * 拉取菜单商店单条详情并写回 `selectedMenu`（与列表项合并）。
     * @param seq - 须与 `menuDetailLoadSeq` 一致时才应用结果
     * @param listFallback - 进入详情时的列表缓存项
     */
    private async fetchMenuStoreMenuDetail(seq: number, listFallback: MenuStoreMenuListItem) {
        const menuId = listFallback._id;
        runInAction(() => {
            this.menuDetailLoading = true;
        });
        try {
            const res = await apiManage.getMenuStoreMenuById(menuId);
            if (_.get(res, 'error')) {
                const msg = _.get(res, 'result.message') || _.get(res, 'result.details') || '';
                GlobalNotification.error(String(msg || i18n.t('menu_store_msg_load_detail_failed')));
                return;
            }
            const parsed = parseMenuStoreMenuDetailResponse(res);
            if (!parsed) {
                GlobalNotification.error(i18n.t('menu_store_msg_load_detail_failed'));
                return;
            }
            runInAction(() => {
                if (seq !== this.menuDetailLoadSeq) {
                    return;
                }
                /** 进入详情后可能被列表同步更新，以当前 `selectedMenu` 为合并基底 */
                const base = this.selectedMenu?._id === menuId ? this.selectedMenu : listFallback;
                if (base._id !== menuId) {
                    return;
                }
                this.selectedMenu = mergeMenuStoreListItemWithDetail(base, parsed);
            });
        } finally {
            runInAction(() => {
                if (seq === this.menuDetailLoadSeq) {
                    this.menuDetailLoading = false;
                }
            });
        }
    }

    /**
     * 从菜单详情进入菜品详情。
     */
    openDishDetail(dish: MenuStoreTemplateMenuRow) {
        this.selectedDish = dish;
        this.viewMode = 'dish-detail';
    }

    /**
     * 从菜品详情返回菜单详情。
     */
    backToMenuDetail() {
        if (!this.selectedMenu) {
            this.viewMode = 'list';
            return;
        }
        this.viewMode = 'detail';
        this.selectedDish = null;
    }

    /**
     * 返回列表视图。
     * @param options - `skipMyMenusListReload` 为 true 时不请求我的菜单列表（例如已先执行 `refreshMyMenusListsFromFirstPage`）。
     */
    backToList(options?: { skipMyMenusListReload?: boolean }) {
        this.menuDetailLoadSeq += 1;
        this.menuDetailLoading = false;
        this.selectedMenu = null;
        this.selectedDish = null;
        this.viewMode = 'list';
        this.resetLicenseCodeUiState();
        if (this.rootView === 'store-home') {
            void this.loadBrowseList();
        } else if (!options?.skipMyMenusListReload) {
            void this.reloadMyMenusCurrentTabFirstPage();
        }
    }

    /**
     * 清空授权码弹窗相关内存态（切换菜单或返回列表时调用）。
     */
    resetLicenseCodeUiState() {
        this.licenseCodeRows = [];
        this.licenseCodesLoading = false;
        this.generateLicenseSubmitting = false;
        this.displayGeneratedLicenseCodes = [];
    }

    /**
     * 拉取当前菜单的授权码列表（Apifox：`GET /v3/menu-store/menu/{id}/license-codes`）。
     * @param menuId - 菜单商店文档 `_id`
     */
    async fetchMenuStoreLicenseCodes(menuId: string) {
        this.licenseCodesLoading = true;
        try {
            const res = await apiManage.getMenuStoreLicenseCodes(menuId);
            if (_.get(res, 'error')) {
                const msg = _.get(res, 'result.message') || _.get(res, 'result.details') || '';
                GlobalNotification.error(String(msg || i18n.t('global_text_operate_failure')));
                runInAction(() => {
                    this.licenseCodeRows = [];
                });
                return;
            }
            const rows = parseMenuStoreLicenseCodesResponse(res);
            runInAction(() => {
                this.licenseCodeRows = rows;
            });
        } finally {
            runInAction(() => {
                this.licenseCodesLoading = false;
            });
        }
    }

    /**
     * 生成授权码（Apifox：`POST /v3/menu-store/menu/{id}/license-codes`）。
     * 成功体含 `generated` 时将其合并进 `licenseCodeRows`（新行追加到列表末尾、按 `code` 去重），不再二次请求列表 GET。
     * @param menuId - 菜单商店文档 `_id`
     * @param count - 生成条数，默认 1
     */
    async postMenuStoreGenerateLicenseCodes(menuId: string, count = 1) {
        runInAction(() => {
            this.displayGeneratedLicenseCodes = [];
            this.generateLicenseSubmitting = true;
        });
        setLoading(true);
        try {
            const res = await apiManage.postMenuStoreLicenseCodes(menuId, { count });
            if (_.get(res, 'error')) {
                const msg = _.get(res, 'result.message') || _.get(res, 'result.details') || '';
                GlobalNotification.error(String(msg || i18n.t('global_text_operate_failure')));
                return;
            }
            const newRows = parseMenuStoreCreateLicenseCodeRows(res);
            const codes =
                newRows.length > 0 ? newRows.map((r) => r.code) : parseMenuStoreCreateLicenseCodesResponse(res);
            runInAction(() => {
                this.displayGeneratedLicenseCodes = codes;
                if (newRows.length > 0) {
                    /** 已存在的码集合，避免重复合并 */
                    const existingCodes = new Set(this.licenseCodeRows.map((r) => r.code));
                    const toAppend = newRows.filter((r) => !existingCodes.has(r.code));
                    this.licenseCodeRows = [...this.licenseCodeRows, ...toAppend];
                } else if (codes.length > 0) {
                    /** 旧版仅返回码字符串时的兜底：合成 `pending` 行并追加到末尾 */
                    const existingCodes = new Set(this.licenseCodeRows.map((r) => r.code));
                    const synthetic: MenuStoreLicenseCodeRow[] = codes
                        .filter((c) => !existingCodes.has(c))
                        .map((code) => ({ code, status: 'pending' }));
                    this.licenseCodeRows = [...this.licenseCodeRows, ...synthetic];
                }
            });
            GlobalNotification.success(i18n.t('global_text_operate_success'));
        } finally {
            runInAction(() => {
                this.generateLicenseSubmitting = false;
            });
            setLoading(false);
        }
    }

    openActivateDialog() {
        this.activateLicenseInput = '';
        this.activateDialogOpen = true;
    }

    closeActivateDialog() {
        this.activateDialogOpen = false;
        this.activateLicenseInput = '';
    }

    /**
     * 打开「移除菜单」确认弹窗（需当前有选中项且具备移除权限）。
     */
    openRemoveMenuConfirm() {
        const item = this.selectedMenu;
        if (!item || !this.canRemoveMenu(item)) {
            return;
        }
        this.removeMenuConfirmOpen = true;
    }

    /** 关闭「移除菜单」确认弹窗 */
    closeRemoveMenuConfirm() {
        this.removeMenuConfirmOpen = false;
    }

    setActivateLicenseInput(v: string) {
        this.activateLicenseInput = v;
    }

    /** 打开「立刻使用」门店选择弹窗 */
    openUseNowDialog() {
        this.useNowRestaurantStringIds = [];
        this.useNowDialogOpen = true;
    }

    /** 关闭「立刻使用」门店选择弹窗 */
    closeUseNowDialog() {
        this.useNowDialogOpen = false;
        this.useNowRestaurantStringIds = [];
    }

    /** 关闭「立刻使用」结果汇总弹窗并清空展示数据 */
    closeUseNowResultDialog() {
        this.useNowResultDialogOpen = false;
        this.useNowResultSubtitle = '';
        this.useNowResultDialogLines = [];
        this.useNowResultHadSuccessfulSync = false;
    }

    /**
     * 「立刻使用」弹窗：切换某门店的选中状态（再次点击同一行取消选中）。
     * @param restaurantStringId - 门店 `string_id`
     */
    toggleUseNowRestaurantStringId(restaurantStringId: string) {
        const id = restaurantStringId.trim();
        if (!id) {
            return;
        }
        const prev = this.useNowRestaurantStringIds;
        const idx = prev.indexOf(id);
        this.useNowRestaurantStringIds = idx >= 0 ? prev.filter((x) => x !== id) : [...prev, id];
    }

    /**
     * 已激活详情「立刻使用」：确认所选门店后逐店校验隧道，通过的门店一并调用 `POST /v3/menu-store/apply-to-restaurant`；隧道失败的门店跳过不阻断；结束后弹出结果汇总。
     * @param item - 当前商店菜单详情
     * @param storeOptions - 可选门店列表（用于结果文案中的门店名称解析）
     */
    async submitUseNow(item: MenuStoreMenuListItem | null, storeOptions: { value: string; label: string }[]) {
        if (!item?._id) {
            GlobalNotification.error(i18n.t('menu_store_msg_activate_failed'));
            return;
        }
        /** 去重、去空后的目标门店列表 */
        const targetStoreIds = Array.from(new Set(this.useNowRestaurantStringIds.map((s) => s.trim()).filter(Boolean)));
        if (targetStoreIds.length === 0) {
            GlobalNotification.error(i18n.t('menu_store_msg_no_restaurant'));
            return;
        }
        /** 门店 string_id → 展示名称 */
        const labelById = new Map(storeOptions.map((o) => [o.value.trim(), (o.label ?? '').trim() || o.value.trim()]));
        /** 名称分隔符（与菜单库分享、shell2-app 一致） */
        const nameSep = i18n.t('menu_library_share_success_names_sep');
        /** 结果弹窗副标题：菜单名 + 上传日期（与 shell2-app `useNowShareSubtitle` 一致） */
        const menuNameOnly = (item.name ?? '').trim() || '—';
        const datePart = formatMenuStoreDate(item.created_at as string | undefined);
        const resultSubtitle = datePart && datePart !== '—' ? `${menuNameOnly} - ${datePart}` : menuNameOnly;

        this.useNowSubmitting = true;
        setLoading(true);
        try {
            /** 隧道失败门店（保留接口原始错误供 `mapErrorToI18nKey`） */
            const tunnelFailRows: { label: string; tunnelRes: unknown }[] = [];
            /** 隧道通过、参与下发的门店 id */
            const tunnelOkIds: string[] = [];
            for (const storeId of targetStoreIds) {
                const tunnelRes = await apiManage_rbp.checkPermissionByRestaurantStringId(storeId);
                if (_.get(tunnelRes, 'error')) {
                    tunnelFailRows.push({
                        label: labelById.get(storeId) ?? storeId,
                        tunnelRes,
                    });
                } else {
                    tunnelOkIds.push(storeId);
                }
            }

            /** 接口成功后已同步门店的展示名（与 shell2-app `syncedLabels`） */
            let syncedLabels: string[] = [];
            /** 隧道通过但 apply 批量失败时，各门店展示共用的原因（与隧道失败一并列入「以下门店同步失败」连续编号） */
            let apiBatchFailReason: string | null = null;

            if (tunnelOkIds.length > 0) {
                const res = await apiManage.postMenuStoreApplyToRestaurant({
                    menu_id: item._id,
                    restaurant_string_ids: tunnelOkIds,
                });
                if (_.get(res, 'error')) {
                    const msg = _.get(res, 'result.message') || _.get(res, 'result.details') || '';
                    apiBatchFailReason = String(msg || i18n.t('global_text_operate_failure')).trim();
                    GlobalNotification.error(apiBatchFailReason);
                } else {
                    syncedLabels = tunnelOkIds.map((id) => labelById.get(id) ?? id);
                }
            }

            /** 汇总失败行：先隧道不通，再接口批量失败（同一标题下连续编号，与 shell2-app） */
            const syncFailRows: { label: string; reason: string }[] = [];
            tunnelFailRows.forEach((row) => {
                syncFailRows.push({
                    label: row.label,
                    reason: i18n.t(mapErrorToI18nKey(row.tunnelRes)),
                });
            });
            if (apiBatchFailReason && tunnelOkIds.length > 0) {
                const apiReason = apiBatchFailReason;
                tunnelOkIds.forEach((id) => {
                    syncFailRows.push({
                        label: labelById.get(id) ?? id,
                        reason: apiReason,
                    });
                });
            }

            /** 正文行顺序：成功 → 「以下门店同步失败」编号列表 */
            const lines: string[] = [];
            if (syncedLabels.length > 0) {
                lines.push(
                    i18n.t('menu_store_use_now_result_line_synced', {
                        names: syncedLabels.join(nameSep),
                    }),
                );
            }
            if (syncFailRows.length > 0) {
                lines.push(i18n.t('menu_store_use_now_result_tunnel_fail_title'));
                syncFailRows.forEach((row, idx) => {
                    lines.push(
                        i18n.t('menu_store_use_now_result_tunnel_fail_row', {
                            index: idx + 1,
                            name: row.label,
                            reason: row.reason,
                        }),
                    );
                });
            }

            runInAction(() => {
                this.useNowResultSubtitle = resultSubtitle;
                this.useNowResultDialogLines = lines.slice();
                this.useNowResultHadSuccessfulSync = syncedLabels.length > 0;
                this.useNowResultDialogOpen = true;
                this.closeUseNowDialog();
            });
        } finally {
            runInAction(() => {
                this.useNowSubmitting = false;
            });
            setLoading(false);
        }
    }

    /**
     * 提交激活（仅传授权码；目标餐厅由后端按登录上下文解析，与 Apifox 一致）。
     * 成功后提示「激活成功」国际化文案，关闭弹窗并**留在当前详情**；重新拉取单条详情与对应列表以便展示「已激活」等状态。
     */
    async submitActivate() {
        const code = this.activateLicenseInput.trim();
        if (!code) {
            GlobalNotification.error(i18n.t('menu_store_msg_license_required'));
            return;
        }
        this.activateSubmitting = true;
        setLoading(true);
        try {
            const res = await apiManage.postMenuStoreActivate({
                license_code: code,
            });
            if (_.get(res, 'error')) {
                const msg = _.get(res, 'result.message') || _.get(res, 'result.details') || '';
                GlobalNotification.error(String(msg || i18n.t('menu_store_msg_activate_failed')));
                return;
            }
            GlobalNotification.success(i18n.t('menu_store_msg_activate_success'));
            this.closeActivateDialog();
            /** 停留在当前详情页：合并最新详情（如 `purchased_by_me`），并后台刷新列表数据 */
            const fallback = this.selectedMenu;
            if (fallback?._id) {
                this.menuDetailLoadSeq += 1;
                const seq = this.menuDetailLoadSeq;
                void this.fetchMenuStoreMenuDetail(seq, fallback);
            }
            if (this.rootView === 'store-home') {
                void this.loadBrowseList();
            } else {
                void this.refreshMyMenusListsFromFirstPage();
            }
        } finally {
            runInAction(() => {
                this.activateSubmitting = false;
            });
            setLoading(false);
        }
    }

    /**
     * 当前登录用户 id（与 `created_by` 比较）
     */
    get currentAccountId(): string | undefined {
        const id = accountState.userInfo?._id;
        return id != null ? String(id) : undefined;
    }

    /**
     * 根据后端 **`created_by`**、**`purchased_by_me`** 与进入路径，解析详情应采用的展示口径（与 `MenuStoreDetailView` 的 `detailSource` 同枚举）。
     * 优先级：**创建者** → `my-uploaded`；否则 **`purchased_by_me === true`** → `my-activated`；否则回退 **`detailSource`**（列表未带字段时的兼容）。
     * @param item - 当前菜单项（列表或详情合并后）
     */
    resolveDetailSourceForItem(item: MenuStoreMenuListItem): 'store-home' | 'my-activated' | 'my-uploaded' {
        const uid = this.currentAccountId;
        /** 制作人 id 字符串，与当前账号比对 */
        const createdBy = item.created_by != null ? String(item.created_by) : '';
        if (uid && createdBy !== '' && createdBy === uid) {
            return 'my-uploaded';
        }
        if (item.purchased_by_me === true) {
            return 'my-activated';
        }
        return this.detailSource;
    }

    /**
     * 当前详情页解析后的展示口径（依赖 `selectedMenu` 上后端字段）。
     */
    get resolvedDetailSource(): 'store-home' | 'my-activated' | 'my-uploaded' {
        if (!this.selectedMenu) {
            return this.detailSource;
        }
        return this.resolveDetailSourceForItem(this.selectedMenu);
    }

    /**
     * 解析 `PATCH .../status` 的 **`remove_context`**（Apifox 必填）：购买方从已激活详情移除为 **`purchased`**，其余下架为 **`uploaded`**。
     * @param item - 当前菜单项
     */
    private resolveMenuStoreRemoveContext(item: MenuStoreMenuListItem): 'uploaded' | 'purchased' {
        const uid = this.currentAccountId;
        /** 是否为文档创建者（与 `canRemoveMenu` 口径一致） */
        const isCreator = uid != null && item.created_by != null && String(item.created_by) === uid;
        /** 与详情展示一致的来源，用于购买方分支 */
        const src = this.resolveDetailSourceForItem(item);
        if (!isCreator && src === 'my-activated') {
            return 'purchased';
        }
        return 'uploaded';
    }

    /**
     * 是否可点「移除菜单」：创建者下架（需 `published`）；已激活视角下非创建者亦可（同接口，后端仅移除本人记录）。
     * @param item - 当前菜单项（须含后端 `created_by` / `purchased_by_me` 或依赖进入路径回退）
     */
    canRemoveMenu(item: MenuStoreMenuListItem): boolean {
        const uid = this.currentAccountId;
        if (!uid) {
            return false;
        }
        /** 当前用户是否为文档创建者 */
        const isCreator = item.created_by != null && String(item.created_by) === uid;
        if (isCreator) {
            return item.status === 'published';
        }
        if (this.resolveDetailSourceForItem(item) === 'my-activated') {
            return item.status === 'published' || item.status === 'delisted';
        }
        return false;
    }

    /**
     * 列表刷新后从「我的菜单」缓存回写当前详情（分组时在 purchased / uploaded 中查找）。
     * @param menuId - 菜单 `_id`
     */
    syncSelectedMenuFromMyMenus(menuId: string) {
        const found =
            this.myMenusPurchasedList.find((m) => m._id === menuId) ??
            this.myMenusUploadedList.find((m) => m._id === menuId);
        if (found && this.selectedMenu?._id === menuId) {
            runInAction(() => {
                this.selectedMenu = found;
            });
        }
    }

    /**
     * 移除菜单：`PATCH /v3/menu-store/menu/{id}/status` + `delisted`。
     * 创建者 → 全局下架；已激活购买方 → 后端仅移除本人侧记录（响应可选 `effect`）。
     * @param item - 当前菜单项
     */
    async removeMenuFromStore(item: MenuStoreMenuListItem) {
        if (!this.canRemoveMenu(item)) {
            return;
        }
        this.removingMenuId = item._id;
        setLoading(true);
        try {
            const remove_context = this.resolveMenuStoreRemoveContext(item);
            const res = await apiManage.patchMenuStoreMenuStatus(item._id, {
                status: 'delisted',
                remove_context,
            });
            if (_.get(res, 'error')) {
                const msg = _.get(res, 'result.message') || _.get(res, 'result.details') || '';
                GlobalNotification.error(String(msg || i18n.t('menu_store_msg_remove_failed')));
                return;
            }
            GlobalNotification.success(i18n.t('global_text_operate_success'));
            if (this.rootView === 'my-menus') {
                await this.refreshMyMenusListsFromFirstPage();
            }
            if (this.selectedMenu?._id === item._id) {
                this.backToList({ skipMyMenusListReload: true });
            }
        } finally {
            runInAction(() => {
                this.removingMenuId = null;
            });
            setLoading(false);
        }
    }

    /**
     * 保存编辑预览：一次 `PATCH .../dish-visibility` 提交全部菜品（打码 ↔ `is_detail_visible`）。
     * @param item - 当前菜单
     * @param maskMap - `true` 为打码
     * @returns 是否成功
     */
    async saveMenuStoreDishVisibility(
        item: MenuStoreMenuListItem | null,
        maskMap: Record<string, boolean>
    ): Promise<boolean> {
        if (!item?._id) {
            return false;
        }
        const total = countMenuStoreStructureDishes(item);
        if (total === 0) {
            GlobalNotification.error(i18n.t('menu_store_msg_dish_visibility_no_dishes'));
            return false;
        }
        const items = collectMenuStoreDishVisibilityPatches(item, maskMap);
        if (items.length !== total) {
            GlobalNotification.error(i18n.t('menu_store_msg_dish_visibility_missing_id'));
            return false;
        }
        this.previewSaveSubmitting = true;
        setLoading(true);
        try {
            const res = await apiManage.patchMenuStoreDishVisibility(item._id, { items });
            if (_.get(res, 'error')) {
                const msg = _.get(res, 'result.message') || _.get(res, 'result.details') || '';
                GlobalNotification.error(String(msg || i18n.t('global_text_operate_failure')));
                return false;
            }
            GlobalNotification.success(i18n.t('global_text_operate_success'));
            if (this.rootView === 'my-menus') {
                await this.refreshMyMenusListsFromFirstPage();
            }
            /**
             * 列表接口返回的卡片项通常不含完整 `structure`；若用 `syncSelectedMenuFromMyMenus` 写回 `selectedMenu`，
             * 菜品栅格会变为空（展示「暂无更多数据」）。保存成功后按 Apifox 再拉 `GET .../menu/{id}` 合并详情。
             */
            if (this.viewMode === 'detail' && this.selectedMenu?._id === item._id) {
                const listMeta =
                    this.myMenusPurchasedList.find((m) => m._id === item._id) ??
                    this.myMenusUploadedList.find((m) => m._id === item._id);
                const fallback = listMeta ?? this.selectedMenu ?? item;
                this.menuDetailLoadSeq += 1;
                const seq = this.menuDetailLoadSeq;
                void this.fetchMenuStoreMenuDetail(seq, fallback);
            }
            return true;
        } finally {
            runInAction(() => {
                this.previewSaveSubmitting = false;
            });
            setLoading(false);
        }
    }
}
